import Grid from '@mui/material/Grid';
import { InvalidItems } from '@openx/components-core/lib/TransferList/bulk/InvalidItems/InvalidItems.tsx';
import { ItemsOverLimit } from '@openx/components-core/lib/TransferList/bulk/ItemsOverLimit/ItemsOverLimit.tsx';
import { DOMAINS_LIMIT } from '@openx/components-targeting/Targeting/Domains/constants.ts';
import { INVENTORY_LIMITS } from '@openx/components-targeting/Targeting/shared/InventoryAndContent/constants.ts';
import {
  Actions,
  FormTypes,
  type FormTypesKey,
  InventoryListFormFields,
  type InventoryListFormState,
} from '@openx/types/inventoryList.ts';
import { AppInventoryType } from '@openx/types/targeting/inventoryAndContent.ts';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { InventoryListFlatList } from './InventoryListFlatList.tsx';
import { InventoryListTextarea } from './InventoryListTextarea.tsx';
import { useHandleBulkState } from './useHandleBulkState.ts';

type InventoryListsBulkAddProps = {
  type: FormTypesKey;
};

export const InventoryListsBulkAdd = ({ type }: InventoryListsBulkAddProps) => {
  const {
    state,
    dispatch,
    handleBulkAdd,
    handleBulkValidation,
    handleInputChange,
    handleRemoveAll,
    handleRemoveItem,
    handleBulkReplace,
  } = useHandleBulkState(type);
  const { setValue, clearErrors } = useFormContext<InventoryListFormState>();
  const listTypeRef = useRef(type);

  useEffect(() => {
    if (listTypeRef.current !== type) {
      listTypeRef.current = type;
      dispatch({ type: Actions.RESET_FORM });
      clearErrors(InventoryListFormFields.VALUES);
      setValue(InventoryListFormFields.VALUES, [], { shouldDirty: true, shouldValidate: false });
    }
  }, [type, setValue, clearErrors, dispatch]);

  const hasErrors = !!state.bulkErrors.length;

  const typeLimits: Record<FormTypesKey, number> = {
    [FormTypes.BUNDLES]: INVENTORY_LIMITS[AppInventoryType.APP_BUNDLE_ID],
    [FormTypes.DOMAINS]: DOMAINS_LIMIT,
  };
  const currentItemsCount = state.validItems.length + state.listData.length;
  const hasItemsOverLimit = currentItemsCount > typeLimits[type];

  return (
    <Grid
      sx={{
        mt: 4,
      }}
    >
      {(hasErrors || hasItemsOverLimit) && (
        <Grid
          container
          size={6}
          spacing={0.5}
          sx={{
            mb: 5,
            mt: -2.5,
          }}
        >
          {hasErrors && <InvalidItems errors={state.bulkErrors} />}
          {hasItemsOverLimit && (
            <ItemsOverLimit
              handleBulkReplace={handleBulkReplace}
              items={state.validItems}
              listData={state.listData}
              limit={typeLimits[type]}
            />
          )}
        </Grid>
      )}
      <Grid container spacing={2}>
        <InventoryListTextarea
          type={type}
          typeLimits={typeLimits}
          bulkInput={state.bulkInput}
          validItems={state.validItems}
          listData={state.listData}
          handleBulkValidation={handleBulkValidation}
          handleBulkAdd={handleBulkAdd}
          handleInputChange={handleInputChange}
        />

        <InventoryListFlatList
          listData={state.listData}
          handleRemoveAll={handleRemoveAll}
          handleRemoveItem={handleRemoveItem}
        />
      </Grid>
    </Grid>
  );
};
